"use client";

import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import { Button } from "../../../elements/button/button";
import { Image } from "../../../elements/image/image";
import { TextD2C } from "../../../elements/text-d2c/text";
import { cn } from "../../../lib/utils";
import type { MainNavigationTeaser } from "../main-navigation.types";

export interface NavigationTeaserProps {
  teaser: MainNavigationTeaser;
  /** Compact tiles use smaller text, e.g. the full width mobile teaser. */
  variant: "default" | "compact";
  /** Square in the desktop mega menu, wide in the mobile menu. */
  aspectRatio: "1:1" | "16:9";
  /** Rendered image width, passed to the image `sizes` attribute. */
  sizes: string;
  onLinkClick?: () => void;
}

/**
 * Square promotional tile. When the teaser has text, the text names the link
 * and the image is decorative; otherwise the image alt text names the link.
 */
export function NavigationTeaser(props: Readonly<NavigationTeaserProps>) {
  const isCompact = props.variant === "compact";
  const hasText = Boolean(
    props.teaser.eyebrow ||
      props.teaser.headline?.json ||
      props.teaser.description
  );

  return (
    <Button
      className="group relative block w-full overflow-hidden whitespace-normal text-left text-white focus-visible:outline-2 focus-visible:outline-black focus-visible:outline-solid focus-visible:outline-offset-4"
      href={props.teaser.link.href}
      mode="D2C"
      onClick={props.onLinkClick}
      target={props.teaser.link.target}
      unstyled={true}
    >
      <Image
        alt={props.teaser.image.alt || props.teaser.link.label}
        aspectRatio={props.aspectRatio}
        decorative={hasText}
        fill={true}
        loading="lazy"
        objectFit="cover"
        sizes={props.sizes}
        src={props.teaser.image.src}
      />
      {hasText ? (
        // The gradient keeps white text readable on light images.
        <div
          className={cn(
            "absolute inset-x-0 top-0 break-words bg-linear-to-b from-black/40 to-transparent group-hover:underline",
            isCompact ? "p-3 pb-10" : "p-4 pb-16"
          )}
        >
          {props.teaser.eyebrow ? (
            <TextD2C color="white" style="uppercase" tagName="p" variant="h6">
              {props.teaser.eyebrow}
            </TextD2C>
          ) : null}
          {props.teaser.headline?.json ? (
            <TextD2C
              color="white"
              style="no-mb"
              tagName="p"
              variant={isCompact ? "h5" : "h4"}
            >
              <RichTextRenderer richText={props.teaser.headline.json} />
            </TextD2C>
          ) : null}
          {props.teaser.description ? (
            <div className="mt-2">
              <TextD2C
                color="white"
                tagName="p"
                variant={isCompact ? "p-small" : "p"}
              >
                {props.teaser.description}
              </TextD2C>
            </div>
          ) : null}
        </div>
      ) : null}
    </Button>
  );
}
